import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo, LogoMark } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import api from "@/lib/api";
import {
  Loader2, User, Bike, UtensilsCrossed, MapPin, ChevronLeft,
  Wrench, Package, Pill, ShoppingBasket, Hotel, Building2, Briefcase,
} from "lucide-react";

const formatError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(", ");
  return JSON.stringify(d);
};

// Customer path is one role. Business Owner path has 8 sub-types.
const BUSINESS_TYPES = [
  { role: "restaurant_owner", key: "restaurant_owner", label_en: "Restaurant", label_ar: "مطعم", icon: UtensilsCrossed, accent: "#FF6B00", desc_en: "Sell food on ServeOn", desc_ar: "بع الطعام على سيرف أون" },
  { role: "provider", key: "pharmacy",      label_en: "Pharmacy",      label_ar: "صيدلية", icon: Pill, accent: "#22C55E", desc_en: "Fulfill prescriptions", desc_ar: "تنفيذ الوصفات الطبية" },
  { role: "provider", key: "grocery",       label_en: "Grocery",       label_ar: "بقالة", icon: ShoppingBasket, accent: "#84CC16", desc_en: "Sell groceries & essentials", desc_ar: "بع البقالة والأساسيات" },
  { role: "provider", key: "home_services", label_en: "Home Services", label_ar: "خدمات منزلية", icon: Wrench, accent: "#14B8A6", desc_en: "Plumber, electrician, cleaner", desc_ar: "سباك، كهربائي، منظف" },
  { role: "provider", key: "hotels",        label_en: "Hotel",         label_ar: "فندق", icon: Hotel, accent: "#7E22CE", desc_en: "List rooms & suites", desc_ar: "اعرض الغرف والأجنحة" },
  { role: "provider", key: "flats",         label_en: "Flat / Rental", label_ar: "شقة / إيجار", icon: Building2, accent: "#4F46E5", desc_en: "Short/long-term rentals", desc_ar: "إيجارات قصيرة/طويلة" },
  { role: "provider", key: "courier",       label_en: "Courier",       label_ar: "توصيل طرود", icon: Package, accent: "#EAB308", desc_en: "Package & document delivery", desc_ar: "توصيل طرود ووثائق" },
  { role: "provider", key: "business",      label_en: "Business Services", label_ar: "خدمات أعمال", icon: Briefcase, accent: "#64748B", desc_en: "B2B services & consulting", desc_ar: "خدمات B2B واستشارات" },
  { role: "rider",    key: "rider",         label_en: "Delivery Rider", label_ar: "سائق توصيل", icon: Bike, accent: "#F97316", desc_en: "Deliver food & packages", desc_ar: "وصّل الطعام والطرود" },
  { role: "passenger_driver_waitlist", key: "passenger_driver", label_en: "Passenger Driver", label_ar: "سائق ركاب", icon: Bike, accent: "#8B5CF6", desc_en: "Drive people — join waitlist", desc_ar: "وصّل الركاب — قائمة الانتظار" },
];

export default function Register({ businessOnly = false }) {
  const { register } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(businessOnly ? "business" : "category"); // "category" | "business" | "form"
  const [selection, setSelection] = useState(null); // { role, providerModule?, label, icon, accent }
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pw, setPw] = useState("");
  const [state, setState] = useState("khartoum");
  const [allStates, setAllStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/states").then(r => setAllStates(r.data));
    const preRole = sp.get("role");
    if (preRole === "customer") {
      setSelection({ role: "customer", label: lang === "ar" ? "عميل" : "Customer", icon: User, accent: "#FF6B00" });
      setStep("form");
    } else if (preRole) {
      const b = BUSINESS_TYPES.find(t => t.key === preRole || t.role === preRole);
      if (b) {
        setSelection({
          role: b.role,
          providerModule: b.role === "provider" ? b.key : undefined,
          label: lang === "ar" ? b.label_ar : b.label_en,
          icon: b.icon,
          accent: b.accent,
        });
        setStep("form");
      }
    }
  }, [sp, lang]);

  const pickCustomer = () => {
    setSelection({ role: "customer", label: lang === "ar" ? "عميل" : "Customer", icon: User, accent: "#FF6B00" });
    setStep("form");
  };
  const pickBusinessType = (b) => {
    // Passenger driver = waitlist only for now (Uber-like ride hailing coming later)
    if (b.role === "passenger_driver_waitlist") {
      setStep("passenger_waitlist");
      return;
    }
    setSelection({
      role: b.role,
      providerModule: b.role === "provider" ? b.key : undefined,
      label: lang === "ar" ? b.label_ar : b.label_en,
      icon: b.icon,
      accent: b.accent,
    });
    setStep("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selection) return;
    setLoading(true); setErr("");
    try {
      const payload = { name, email, phone, password: pw, role: selection.role, state };
      if (selection.role === "provider") payload.provider_module = selection.providerModule;
      const u = await register(payload);
      const target =
        u.role === "customer" ? "/app" :
        u.role === "restaurant_owner" ? "/restaurant" :
        u.role === "rider" ? "/rider" :
        u.role === "provider" ? "/provider" :
        "/";
      navigate(target, { replace: true });
    } catch (e) {
      setErr(formatError(e));
    } finally { setLoading(false); }
  };

  const Icon = selection?.icon;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="register-page">
      <div className="lg:flex-1 so-gradient-hero so-grain text-white p-8 md:p-16 flex flex-col justify-between">
        <Link to="/"><Logo size={36} /></Link>
        <div className="my-12 lg:my-0">
          <LogoMark size={72} animated />
          <h2 className="mt-6 text-3xl md:text-5xl font-bold leading-tight">{lang === "ar" ? "انضم لسيرف أون." : "Join ServeOn."}</h2>
          <p className="mt-3 text-white/70 max-w-md">
            {lang === "ar"
              ? "سواء كنت عميلًا، مطعمًا، سائقًا، أو مزود خدمة — حسابك هو بوابتك لكل الخدمات."
              : "Whether you're a customer, a restaurant, a rider, or a service provider — your account is the gateway to every service."}
          </p>
        </div>
        <div className="text-xs text-white/60">© 2026 ServeOn</div>
      </div>

      <div className="lg:flex-1 flex flex-col">
        <div className="flex justify-end gap-2 p-4"><LanguageToggle /><ThemeToggle /></div>
        <div className="flex-1 flex items-center justify-center px-6 pb-10">
          <div className="w-full max-w-2xl">
            {step === "category" ? (
              <div data-testid="register-category-picker">
                <h1 className="text-3xl font-bold">{t("signup")}</h1>
                <p className="text-sm text-[var(--so-text-soft)] mt-1">
                  {lang === "ar" ? "كيف تريد استخدام سيرف أون؟" : "How will you use ServeOn?"}
                </p>

                {/* Two big cards: Customer vs Business Owner */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={pickCustomer}
                    data-testid="category-customer"
                    className="group p-6 rounded-2xl border-2 border-[var(--so-border)] hover:border-[var(--so-orange)] transition-all text-start"
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(255,107,0,0.12)" }}>
                      <User className="w-6 h-6 text-[var(--so-orange)]" />
                    </div>
                    <div className="font-bold text-lg">{lang === "ar" ? "عميل" : "Customer"}</div>
                    <div className="text-xs text-[var(--so-text-soft)] mt-1 leading-relaxed">
                      {lang === "ar" ? "اطلب طعامًا، أدوية، بقالة، خدمات منزلية والمزيد." : "Order food, medicine, groceries, home services and more."}
                    </div>
                    <div className="mt-4 text-xs font-semibold text-[var(--so-orange)] opacity-0 group-hover:opacity-100 transition-opacity">
                      {lang === "ar" ? "متابعة →" : "Continue →"}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep("business")}
                    data-testid="category-business"
                    className="group p-6 rounded-2xl border-2 border-[var(--so-border)] hover:border-[var(--so-orange)] transition-all text-start"
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(30,90,158,0.12)" }}>
                      <Briefcase className="w-6 h-6" style={{ color: "#1E5A9E" }} />
                    </div>
                    <div className="font-bold text-lg">{lang === "ar" ? "صاحب عمل" : "Business Owner"}</div>
                    <div className="text-xs text-[var(--so-text-soft)] mt-1 leading-relaxed">
                      {lang === "ar" ? "مطعم، صيدلية، بقالة، فندق، خدمات منزلية، توصيل والمزيد." : "Restaurant, pharmacy, grocery, hotel, home services, delivery and more."}
                    </div>
                    <div className="mt-4 text-xs font-semibold text-[var(--so-orange)] opacity-0 group-hover:opacity-100 transition-opacity">
                      {lang === "ar" ? "اختر نوع عملك →" : "Pick your business type →"}
                    </div>
                  </button>
                </div>

                <div className="mt-6 text-sm text-center text-[var(--so-text-soft)]">
                  {lang === "ar" ? "لديك حساب؟" : "Already have an account?"}{" "}
                  <Link to="/login" className="text-[var(--so-orange)] font-semibold" data-testid="login-link">{t("login")}</Link>
                </div>
              </div>
            ) : step === "business" ? (
              <div data-testid="register-business-picker">
                <button
                  type="button"
                  onClick={() => { if (businessOnly) { navigate("/"); } else { setStep("category"); } setErr(""); }}
                  className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]"
                  data-testid="back-to-category"
                >
                  <ChevronLeft className="w-4 h-4" /> {lang === "ar" ? "عودة" : "Back"}
                </button>
                <h1 className="text-2xl md:text-3xl font-bold">{lang === "ar" ? "ما نوع عملك؟" : "What's your business?"}</h1>
                <p className="text-sm text-[var(--so-text-soft)] mt-1">
                  {lang === "ar" ? "اختر النوع لنعرض لك اللوحة المناسبة." : "Pick the type so we show you the right dashboard."}
                </p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BUSINESS_TYPES.map((b) => {
                    const I = b.icon;
                    return (
                      <button
                        type="button"
                        key={b.key}
                        onClick={() => pickBusinessType(b)}
                        data-testid={`business-${b.key}`}
                        className="p-4 rounded-xl border-2 border-[var(--so-border)] hover:border-[var(--so-orange)]/60 transition-all text-center"
                      >
                        <I className="w-6 h-6 mx-auto mb-2" style={{ color: b.accent }} />
                        <div className="text-sm font-bold">{lang === "ar" ? b.label_ar : b.label_en}</div>
                        <div className="text-[10px] text-[var(--so-text-soft)] mt-1 leading-tight">{lang === "ar" ? b.desc_ar : b.desc_en}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : step === "passenger_waitlist" ? (
              <PassengerDriverWaitlist onBack={() => setStep("business")} lang={lang} />
            ) : (
              <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto" data-testid="register-form">
                <button type="button" onClick={() => { setStep(selection?.role === "customer" ? "category" : "business"); setErr(""); }} className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--so-text-soft)] hover:text-[var(--so-orange)]" data-testid="back-to-picker">
                  <ChevronLeft className="w-4 h-4" /> {lang === "ar" ? "تغيير النوع" : "Change type"}
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--so-border)]" data-testid="selected-role-chip">
                  {Icon && <Icon className="w-4 h-4" style={{ color: selection.accent }} />}
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: selection.accent }}>
                    {lang === "ar" ? "التسجيل كـ" : "Signing up as"} {selection.label}
                  </span>
                </div>
                <h1 className="text-3xl font-bold mt-3">{t("signup")}</h1>

                <div className="mt-5 space-y-3">
                  <input className="so-input" placeholder={t("name")} value={name} onChange={e => setName(e.target.value)} required data-testid="reg-name" />
                  <input className="so-input" type="email" placeholder={t("email")} value={email} onChange={e => setEmail(e.target.value)} required data-testid="reg-email" />
                  <input className="so-input" placeholder={t("phone")} value={phone} onChange={e => setPhone(e.target.value)} data-testid="reg-phone" />
                  <div className="relative">
                    <MapPin className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-[var(--so-text-soft)] pointer-events-none" />
                    <select
                      value={state}
                      onChange={e => setState(e.target.value)}
                      className="so-input ps-10 appearance-none"
                      data-testid="reg-state"
                      required
                    >
                      {allStates.map(s => (
                        <option key={s.key} value={s.key} disabled={s.status !== "live"}>
                          {lang === "ar" ? s.name_ar : s.name_en} {s.status !== "live" ? "(coming soon)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input className="so-input" type="password" placeholder={t("password")} value={pw} onChange={e => setPw(e.target.value)} required minLength={6} data-testid="reg-password" />
                </div>

                {err && <div className="mt-4 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="register-error">{err}</div>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full justify-center mt-6 inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-50"
                  style={{ background: selection.accent }}
                  data-testid="register-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("continue")}
                </button>

                <div className="mt-6 text-sm text-center text-[var(--so-text-soft)]">
                  {lang === "ar" ? "لديك حساب؟" : "Already have an account?"}{" "}
                  <Link to="/login" className="text-[var(--so-orange)] font-semibold" data-testid="login-link-form">{t("login")}</Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Passenger driver waitlist — collected now, activated when Serv Wasel Ride launches
function PassengerDriverWaitlist({ onBack, lang }) {
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [city, setCity] = React.useState("khartoum");
  const [vehicle, setVehicle] = React.useState("motorcycle");
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");

  const isAr = lang === "ar";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      await api.post("/waitlist", {
        module_key: "ride_hailing",
        email,
        name,
        phone,
        note: `Passenger driver signup — city: ${city}, vehicle: ${vehicle}`,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="passenger-waitlist-success">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "rgba(139,92,246,0.15)" }}>
          <Bike className="w-8 h-8 text-violet-500" />
        </div>
        <h2 className="text-2xl font-bold">{isAr ? "تم التسجيل! 🚀" : "You're signed up! 🚀"}</h2>
        <p className="text-sm text-[var(--so-text-soft)] mt-2 max-w-sm mx-auto">
          {isAr
            ? "سنتواصل معك قريبًا لتفعيل حسابك كسائق سيرف واصل رايد."
            : "We'll be in touch soon to activate your Serv Wasel Rides driver account."}
        </p>
        <button onClick={() => (window.location.href = "/")} className="so-btn-primary mt-6" data-testid="passenger-waitlist-home">
          {isAr ? "العودة للرئيسية" : "Back to home"}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-md mx-auto" data-testid="passenger-waitlist-form">
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-[var(--so-text-soft)] hover:text-violet-500">
        <ChevronLeft className="w-4 h-4" /> {isAr ? "عودة" : "Back"}
      </button>
      <div className="mb-4 p-4 rounded-xl" style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.3)" }}>
        <div className="flex items-center gap-2 mb-1">
          <Bike className="w-5 h-5 text-violet-500" />
          <div className="text-xs font-bold uppercase tracking-wider text-violet-500">{isAr ? "انضم كسائق" : "Drive with us"}</div>
        </div>
        <div className="font-bold">{isAr ? "🚀 كن سائق سيرف واصل رايد" : "🚀 Become a Serv Wasel Rides driver"}</div>
        <div className="text-sm text-[var(--so-text-soft)] mt-1">
          {isAr
            ? "انضم لشبكة سائقي سيرف واصل. سجل الآن وابدأ العمل بمرونة."
            : "Join the Serv Wasel Rides driver network. Sign up now to start earning on your schedule."}
        </div>
      </div>

      <div className="space-y-3">
        <input required value={name} onChange={(e)=>setName(e.target.value)} placeholder={isAr?"الاسم الكامل":"Full name"} className="so-input" data-testid="pd-name" />
        <input required type="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="+249xxxxxxxxx" className="so-input" dir="ltr" data-testid="pd-phone" />
        <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder={isAr?"البريد الإلكتروني":"Email"} className="so-input" dir="ltr" data-testid="pd-email" />
        <select value={city} onChange={(e)=>setCity(e.target.value)} className="so-input" data-testid="pd-city">
          <option value="khartoum">Khartoum</option>
          <option value="omdurman">Omdurman</option>
          <option value="bahri">Bahri</option>
          <option value="port_sudan">Port Sudan</option>
          <option value="wad_madani">Wad Madani</option>
          <option value="kassala">Kassala</option>
          <option value="other">Other</option>
        </select>
        <select value={vehicle} onChange={(e)=>setVehicle(e.target.value)} className="so-input" data-testid="pd-vehicle">
          <option value="motorcycle">{isAr?"دراجة نارية":"Motorcycle"}</option>
          <option value="tuktuk">{isAr?"توك توك":"Tuk-tuk"}</option>
          <option value="car">{isAr?"سيارة":"Car"}</option>
          <option value="van">{isAr?"فان":"Van"}</option>
        </select>
      </div>

      {err && <div className="text-sm text-red-500 mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="pd-error">{err}</div>}

      <button type="submit" disabled={submitting} className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold text-white disabled:opacity-60" style={{ background: "linear-gradient(135deg,#8B5CF6,#7C3AED)" }} data-testid="pd-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Bike className="w-4 h-4"/>}
        {isAr ? "سجل كسائق" : "Sign up as driver"}
      </button>
    </form>
  );
}
